import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import { StepProgressLabel } from '@/components/molecules/StepProgressLabel';
import { ParentStep, Status } from '@/types/steps';

interface Props<T> {
  readonly steps: readonly ParentStep<T>[];
  readonly statuses: Record<string, Status>;
}

export function StepperStepsOverview<T = never>(props: Props<T>): React.ReactElement {
  const { steps, statuses } = props;

  const [prefix, last] = React.useMemo(
    (): [readonly ParentStep<T>[], ParentStep<T>] => [steps.slice(0, -1), steps[steps.length - 1]],
    [steps],
  );

  if (steps.length === 0) {
    throw new Error('StepperStepsOverview: steps must not be empty');
  }

  const render = React.useCallback(
    (parent: ParentStep<T>, separator: React.ReactElement | null = null): React.ReactElement => {
      const { title, children } = parent;
      const containerSx = [styles.container, children.length == 0 && styles.bottomPadding];

      return (
        <Box key={parent.path} sx={containerSx}>
          <StepProgressLabel
            path={parent.path}
            title={title}
            steps={children}
            statuses={statuses}
            separator={separator}
          />
        </Box>
      );
    },
    [statuses],
  );

  return (
    <Paper elevation={0}>
      <Box role="navigation" display="flex" flexDirection="column" padding={3}>
        {prefix.map(
          (step: ParentStep<T>): React.ReactElement => render(step, <Box sx={styles.separator} />),
        )}
        {render(last)}
      </Box>
    </Paper>
  );
}

const styles: Record<string, SystemStyleObject> = {
  container: {
    position: 'relative',
    '&:not(:last-of-type)': {
      mb: 1,
    },
  },
  separator: {
    position: 'absolute',
    left: 7.5,
    top: 39,
    bottom: -17,
    width: '1px',
    bgcolor: 'custom.InactiveBackground',
    borderRadius: 1,
  },
};
